import {
  useCallback, useState,
} from 'react';
import { useOnMount } from '~/_shared/hooks/useOnMount';
import { useTranslation } from '~/_shared/utils/hooks';
import { parseUrl } from '~/_shared/utils/url/url.helpers';
import { AppErrorPageWrapperComponent } from '~/app/pages/errorPage/wrapper/appErrorPageWrapper.component';
import { PageWithLoaderComponent } from '~/app/pages/pageWithLoader.component';
import { salesforceAuth } from './salesforce.repository';

enum SalesforceAuthErrorTypes {
  TokenMissing = 'TokenMissing',
  InvalidToken = 'InvalidToken',
}

export const SalesforceAuthPageContainer = () => {
  const [t] = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<SalesforceAuthErrorTypes | null>(null);

  const [params] = useState(() => {
    const fragments = parseUrl(window.location.href).queryParameters;
    const responseType = fragments?.response_type ?? null;
    const clientId = fragments?.client_id ?? null;
    const redirectUri = fragments?.redirect_uri ? decodeURIComponent(fragments.redirect_uri) : null;
    const state = fragments?.state ? decodeURIComponent(fragments.state) : null;

    return { responseType, clientId, redirectUri, state };
  });

  const onLoad = useCallback(async () => {
    if (!params.responseType || !params.clientId || !params.redirectUri || !params.state) {
      setError(SalesforceAuthErrorTypes.TokenMissing);
      setIsLoading(false);
      return;
    }

    try {
      const response = await salesforceAuth({
        clientId: params.clientId,
        redirectUri: params.redirectUri,
        responseType: params.responseType,
        state: params.state,
      });

      if (response?.redirect_uri) {
        window.location.href = response.redirect_uri;
      }
      else {
        setError(SalesforceAuthErrorTypes.InvalidToken);
      }
    }
    catch (error) {
      console.error(error);
      setError(SalesforceAuthErrorTypes.InvalidToken);
    }

    setIsLoading(false);
  }, [params.clientId, params.redirectUri, params.responseType, params.state]);

  useOnMount(() => {
    onLoad();
  });

  if (error) {
    return (
      <AppErrorPageWrapperComponent>
        {error}
      </AppErrorPageWrapperComponent>
    );
  }

  return (
    <PageWithLoaderComponent isLoading={isLoading}>
      {t('Loading...')}
    </PageWithLoaderComponent>
  );
};
